import { useMemo } from 'react';
import useCourseDetailsApi from './useCourseDetailsApi';

/**
 * ? Curso normalizado que consumen la cabecera y las pestañas.
 * Sustituye a la tupla posicional que antes viajaba en location.state.
 * @typedef {Object} CourseDetailI
 * @property {string} id
 * @property {string} name
 * @property {string} description
 * @property {string|null} categoryName
 * @property {string|null} priorityName
 * @property {string|null} creatorName
 * @property {string|null} creatorPhoto
 * @property {string|null} userCreatedId
 * @property {string|null} thumbnailKey Key S3 cruda, no la URL firmada.
 * @property {string|null} updatedAt
 * @property {boolean} isDraft
 * @property {string|null} expirationDate
 */

/**
 * Aplana la respuesta de findOneCourse (PascalCase de GraphQL) al objeto que
 * usan las pantallas.
 * @param {import('Api/Request/Course/findOneCourse').CourseData} course
 * @returns {CourseDetailI|null}
 */
const normalizeCourse = course => {
  if (!course) return null;

  return {
    id: course.id,
    name: course.name,
    description: course.description,
    categoryName: course.CourseCategory?.name ?? null,
    priorityName: course.Priority?.name ?? null,
    creatorName: course.User?.fullName ?? null,
    creatorPhoto: course.User?.photo ?? null,
    userCreatedId: course.userCreatedId ?? null,
    thumbnailKey: course.coverImage ?? null,
    updatedAt: course.updatedAt ?? null,
    isDraft: !!course.isDraft,
    expirationDate: course.expirationDate ?? null,
  };
};

/**
 * ? Hook para la pantalla de detalle de un curso.
 * @param {Object} params Parámetros de configuración.
 * @param {string} params.courseId ID del curso a consultar.
 * @returns {{ course: CourseDetailI|null, loading: boolean }}
 */
const useCourseDetails = ({ courseId } = {}) => {
  const { course, loading } = useCourseDetailsApi({ courseId });

  return {
    course: useMemo(() => normalizeCourse(course), [course]),
    loading,
  };
};

export { normalizeCourse };
export default useCourseDetails;
